import { useState, useRef, useEffect } from 'react';

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

interface AIChatAssistantProps {
  bedrooms: number;
  bathrooms: number;
  area: number;
  areaUnit: string;
  selectedStyle: string;
  kitchenType: string;
  extras: string[];
}

export default function AIChatAssistant({
  bedrooms,
  bathrooms,
  area,
  areaUnit,
  selectedStyle,
  kitchenType,
  extras,
}: AIChatAssistantProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: "Hi! I'm your AI design assistant. Describe any changes to your floor plan and I'll suggest updates instantly.",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const quickPrompts = [
    'Add a home office',
    'Increase living room size',
    'Suggest a style',
    'Optimize for natural light',
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const generateAIResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();
    
    // Contextual responses based on current config
    if (lowerMessage.includes('office') || lowerMessage.includes('workspace')) {
      return `Based on your ${bedrooms}-bed, ${bathrooms}-bath ${selectedStyle} layout, I suggest adding a home office near the ${extras.includes('Balcony') ? 'balcony for natural light' : 'front of the house for privacy'}. Consider a 120-150 sq ft space with built-in shelving.`;
    }
    
    if (lowerMessage.includes('living room') || lowerMessage.includes('living space')) {
      return `For your ${area} ${areaUnit} ${selectedStyle} design, I recommend expanding the living room by 15-20%. This works well with your ${kitchenType} kitchen layout and creates better flow for entertaining.`;
    }
    
    if (lowerMessage.includes('style') || lowerMessage.includes('design')) {
      const suggestions = ['Modern Minimalist', 'Craftsman', 'Mediterranean', 'Contemporary'];
      const filtered = suggestions.filter(s => s !== selectedStyle);
      return `Given your ${bedrooms} bedrooms and ${bathrooms} bathrooms, I'd suggest exploring ${filtered[0]} or ${filtered[1]} styles. They complement your current ${area} ${areaUnit} layout beautifully.`;
    }
    
    if (lowerMessage.includes('light') || lowerMessage.includes('natural')) {
      const hasBalcony = extras.includes('Balcony');
      const hasSkylight = extras.includes('Skylight');
      return `To maximize natural light in your ${selectedStyle} home, I recommend ${!hasSkylight ? 'adding skylights in the main living areas' : 'positioning your skylights strategically'}${!hasBalcony ? ' and adding a balcony or large windows' : ''}. Consider south-facing windows for optimal brightness.`;
    }
    
    if (lowerMessage.includes('garage') || lowerMessage.includes('parking')) {
      const hasGarage = extras.includes('Garage');
      return hasGarage 
        ? `Your current design includes a garage. I suggest adding a mudroom entrance from the garage to keep the main living areas clean. A 6x8 ft space works perfectly.`
        : `For your ${bedrooms}-bedroom ${selectedStyle} home, I recommend adding a 2-car garage (approximately 400-500 sq ft) with direct access to the kitchen or mudroom.`;
    }
    
    if (lowerMessage.includes('bathroom') || lowerMessage.includes('bath')) {
      return `With ${bedrooms} bedrooms, your ${bathrooms} bathrooms provide a good ratio. Consider adding an ensuite to the master bedroom if not already included, or a powder room near the main entrance for guests.`;
    }
    
    if (lowerMessage.includes('kitchen') || lowerMessage.includes('cooking')) {
      return `Your ${kitchenType} kitchen is a great choice for a ${selectedStyle} home. I suggest adding a pantry (5x6 ft) and considering an island if space allows. This enhances both functionality and entertaining flow.`;
    }
    
    if (lowerMessage.includes('outdoor') || lowerMessage.includes('patio') || lowerMessage.includes('deck')) {
      const hasBalcony = extras.includes('Balcony');
      return `For outdoor living in your ${selectedStyle} design, I recommend ${hasBalcony ? 'expanding your balcony' : 'adding a covered patio'} (12x16 ft minimum) accessible from the living room or kitchen. Perfect for ${area > 2000 ? 'large gatherings' : 'intimate entertaining'}.`;
    }
    
    if (lowerMessage.includes('storage') || lowerMessage.includes('closet')) {
      return `Storage is key in a ${bedrooms}-bedroom home. I suggest adding walk-in closets to bedrooms, a linen closet in the hallway, and utilizing under-stair space if you have a two-story design.`;
    }
    
    if (lowerMessage.includes('energy') || lowerMessage.includes('efficient') || lowerMessage.includes('green')) {
      return `For energy efficiency in your ${area} ${areaUnit} ${selectedStyle} home, consider: proper insulation, energy-efficient windows, LED lighting throughout, and strategic window placement for passive heating/cooling. ${extras.includes('Solar Panels') ? 'Your solar panels are an excellent start!' : 'Adding solar panels would be a great investment.'}`;
    }
    
    // Default contextual response
    return `That's an interesting idea for your ${bedrooms}-bed, ${bathrooms}-bath ${selectedStyle} home (${area} ${areaUnit}). I recommend consulting with an architect to explore how this change would integrate with your ${kitchenType} kitchen and ${extras.length > 0 ? 'existing features like ' + extras.slice(0, 2).join(' and ') : 'current layout'}. Would you like more specific suggestions?`;
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Show typing indicator
    setIsTyping(true);

    // Simulate AI response delay
    setTimeout(() => {
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: generateAIResponse(inputValue),
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 1500 + Math.random() * 1000);
  };

  const handleQuickPrompt = (prompt: string) => {
    setInputValue(prompt);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const dm = typeof window !== 'undefined' && document.documentElement.classList.contains('dark');
  const chatBg = dm ? 'bg-gray-900' : 'bg-gray-50';
  const chatBorder = dm ? 'border-gray-800' : 'border-gray-200';
  const msgBg = dm ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800 border border-gray-200';
  const inputBg = dm ? 'bg-gray-800 text-white border-gray-700 placeholder-gray-500' : 'bg-white text-gray-900 border-gray-300 placeholder-gray-400';
  const headerText = dm ? 'text-white' : 'text-gray-900';
  const subText = dm ? 'text-gray-400' : 'text-gray-500';
  const quickBg = dm ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50';

  return (
    <div className={`h-full flex flex-col ${chatBg}`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${chatBorder}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
            <i className="ri-robot-line text-white text-xl"></i>
          </div>
          <div>
            <h3 className={`font-semibold ${headerText}`}>AI Design Assistant</h3>
            <p className={`text-sm ${subText}`}>Powered by Master Architect AI</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.map((message) => (
          <div key={message.id} className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            {message.type === 'ai' && (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-robot-line text-white text-sm"></i>
              </div>
            )}
            <div className={`flex flex-col gap-1 max-w-[75%] ${message.type === 'user' ? 'items-end' : 'items-start'}`}>
              <div className={`px-4 py-3 rounded-2xl ${message.type === 'user' ? 'bg-teal-600 text-white rounded-br-sm' : `${msgBg} rounded-bl-sm`}`}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
              </div>
              <span className={`text-xs px-2 ${subText}`}>{formatTime(message.timestamp)}</span>
            </div>
            {message.type === 'user' && (
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${dm ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <i className={`ri-user-line text-sm ${dm ? 'text-gray-300' : 'text-gray-600'}`}></i>
              </div>
            )}
          </div>
        ))}
        {isTyping && (
          <div className="flex gap-3 justify-start">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-robot-line text-white text-sm"></i>
            </div>
            <div className={`px-4 py-3 rounded-2xl rounded-bl-sm ${msgBg}`}>
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Prompts */}
      <div className={`px-6 py-3 border-t ${chatBorder}`}>
        <div className="flex flex-wrap gap-2">
          {quickPrompts.map((prompt) => (
            <button key={prompt} onClick={() => handleQuickPrompt(prompt)} className={`px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${quickBg}`}>
              {prompt}
            </button>
          ))}
        </div>
      </div>

      {/* Input Bar */}
      <div className={`px-6 py-4 border-t ${chatBorder}`}>
        <div className="flex gap-3">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe your floor plan changes..."
            className={`flex-1 px-4 py-3 rounded-xl border focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 transition-all text-sm ${inputBg}`}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isTyping}
            className="px-6 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-send-plane-fill"></i>
            <span className="font-medium">Send</span>
          </button>
        </div>
      </div>
    </div>
  );
}